'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import type { MapFocus, MapMarkerData, MapMode, MapSegmentData } from '~/components/map';
import { BywayMapLazy } from '~/components/map/BywayMapLazy';
import { visuallyHidden } from '~/components/Utilities';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useTripId } from '~/hooks/useTripId';
import { api } from '~/libs/api';
import type { TripOverviewItem } from '~/services/engine-ts';
import { useOverviewProgressStore } from '~/stores/tpv2/overviewProgressStore';
import { ITINERARY_ACTIVE_LINE_PX, ITINERARY_SECTION_ID } from '../hooks/useItineraryScrollProgress';
import { scrollItineraryCardIntoView } from '../lib/scrollItineraryCardIntoView';
import { tripOverviewToMapData } from '../lib/tripOverviewToMapData';
import { ExpandedItineraryScroller, type ExpandedItineraryScrollerHandle } from './ExpandedItineraryScroller';
const parseMarkerId = (id: string): {
  type: 'origin' | 'destination' | 'stay' | 'stop';
  itemId?: number;
} => {
  if (id === 'origin' || id === 'destination') return {
    type: id
  };
  const [type, idStr] = id.split('-');
  return {
    type: type as 'stay' | 'stop',
    itemId: Number.parseInt(idStr, 10)
  };
};
const parseSegmentId = (id: string): number => Number.parseInt(id.split('-')[1], 10);
const MarkerTooltip = t0 => {
  const $ = _c(14);
  const {
    item,
    fallbackName
  } = t0;
  const t = useTranslations("journey");
  if (!item || item.type === "leg") {
    let t1;
    if ($[0] !== fallbackName) {
      t1 = <strong>{fallbackName}</strong>;
      $[0] = fallbackName;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (item.type === "stay") {
    let t1;
    if ($[2] !== item.place.name) {
      t1 = <strong>{item.place.name}</strong>;
      $[2] = item.place.name;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== item.nights || $[5] !== t) {
      t2 = t("nights_count", {
        count: item.nights
      });
      $[4] = item.nights;
      $[5] = t;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== t2) {
      t3 = <div>{t2}</div>;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== t1 || $[10] !== t3) {
      t4 = <>{t1}{t3}</>;
      $[9] = t1;
      $[10] = t3;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return t4;
  }
  let t1;
  if ($[12] !== item.place.name) {
    t1 = <strong>{item.place.name}</strong>;
    $[12] = item.place.name;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  return t1;
};
const SegmentTooltip = t0 => {
  const $ = _c(14);
  const {
    leg
  } = t0;
  const tAria = useTranslations("aria");
  if (leg.type !== "leg") {
    return null;
  }
  const subtitle = leg.transportMode ?? "";
  const t1 = leg.origin.name;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span aria-hidden="true"> → </span>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== tAria) {
    t3 = tAria("to");
    $[1] = tAria;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = ` ${t3} `;
  let t5;
  if ($[3] !== t4) {
    t5 = <span className={visuallyHidden}>{t4}</span>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== leg.destination.name || $[6] !== leg.origin.name || $[7] !== t5) {
    t6 = <strong>{t1}{t2}{t5}{leg.destination.name}</strong>;
    $[5] = leg.destination.name;
    $[6] = leg.origin.name;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== subtitle) {
    t7 = subtitle ? <div>{subtitle}</div> : null;
    $[9] = subtitle;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
interface InlineMapProps {
  mode: MapMode;
  onModeChange: (mode: MapMode) => void;
  sectionId?: string;
  variant?: 'overview' | 'transport';
}
export function InlineMap(t0) {
  const $ = _c(34);
  const {
    mode,
    onModeChange,
    sectionId: t1,
    variant: t2
  } = t0;
  const sectionId = t1 === undefined ? ITINERARY_SECTION_ID : t1;
  const variant = t2 === undefined ? "overview" : t2;
  const isTransport = variant === "transport";
  const tripId = useTripId();
  let t3;
  if ($[0] !== tripId) {
    t3 = {
      tripId
    };
    $[0] = tripId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = !!tripId;
  let t5;
  if ($[2] !== t4) {
    t5 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: t4,
      keepPreviousData: true
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    data
  } = api.trip.getOverview.useQuery(t3, t5);
  const [focus, setFocusState] = useState(null);
  const scrollerRef = useRef(null);
  const mapData = data ? tripOverviewToMapData(data.items, {
    middleMarkers: isTransport ? "legs" : "stays"
  }) : null;
  const progress = useOverviewProgressStore(_temp) ?? undefined;
  const activeItemId = useOverviewProgressStore(_temp2);
  let t6;
  if ($[4] !== sectionId) {
    t6 = target => {
      const section = document.getElementById(sectionId);
      if (!section) {
        return;
      }
      const rect = section.getBoundingClientRect();
      const targetSectionTop = ITINERARY_ACTIVE_LINE_PX - target * rect.height;
      window.scrollBy({
        top: rect.top - targetSectionTop,
        behavior: "smooth"
      });
    };
    $[4] = sectionId;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const scrollToProgress = t6;
  const scrollMarkerCardIntoView = _temp3;
  const setFocus = next => {
    setFocusState(next);
    if (next?.kind === "marker" && mapData) {
      const marker = mapData.markers.find(m => m.id === next.id);
      if (marker?.progressAt === undefined) {
        return;
      }
      if (mode === "expanded") {
        scrollerRef.current?.scrollToProgress(marker.progressAt);
      } else {
        if (!scrollMarkerCardIntoView(next.id)) {
          scrollToProgress(marker.progressAt);
        }
      }
    }
  };
  if (!mapData || !data) {
    return null;
  }
  let itemsById;
  if ($[6] !== data.items) {
    itemsById = new Map();
    for (const item of data.items) {
      itemsById.set(item.itemId, item);
    }
    $[6] = data.items;
    $[7] = itemsById;
  } else {
    itemsById = $[7];
  }
  let t7;
  if ($[8] !== itemsById) {
    t7 = marker_0 => {
      const parsed = parseMarkerId(marker_0.id);
      const item_0 = parsed.itemId !== undefined ? itemsById.get(parsed.itemId) : undefined;
      return {
        ...marker_0,
        tooltip: <MarkerTooltip item={item_0} fallbackName={marker_0.label ?? ""} />
      };
    };
    $[8] = itemsById;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const markersWithTooltips = mapData.markers.map(t7);
  let t8;
  if ($[10] !== activeItemId || $[11] !== itemsById) {
    t8 = activeItemId ? itemsById.get(Number(activeItemId)) : undefined;
    $[10] = activeItemId;
    $[11] = itemsById;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const activeItem = t8;
  const activePlace = activeItem ? activeItem.type === "leg" ? activeItem.origin : activeItem.place : undefined;
  const activeMarkerId = activePlace ? mapData.markers.find(m_0 => m_0.coordinates.latitude === activePlace.geoCoordinates.latitude && m_0.coordinates.longitude === activePlace.geoCoordinates.longitude)?.id ?? null : null;
  let t9;
  if ($[13] !== itemsById) {
    t9 = segment => {
      const leg = itemsById.get(parseSegmentId(segment.id));
      return {
        ...segment,
        tooltip: leg ? <SegmentTooltip leg={leg} /> : null
      };
    };
    $[13] = itemsById;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const segmentsWithTooltips = mapData.segments.map(t9);
  const t10 = mode !== "detailed";
  const t11 = isTransport && mode === "compact";
  const t12 = !isTransport;
  let t13;
  if ($[15] !== data || $[16] !== isTransport || $[17] !== mode || $[18] !== scrollerRef) {
    t13 = mode === "expanded" && data ? <ExpandedItineraryScroller ref={scrollerRef} items={data.items} legsOnly={isTransport} /> : null;
    $[15] = data;
    $[16] = isTransport;
    $[17] = mode;
    $[18] = scrollerRef;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== activeMarkerId || $[21] !== focus || $[22] !== mapData.route || $[23] !== markersWithTooltips || $[24] !== mode || $[25] !== onModeChange || $[26] !== progress || $[27] !== segmentsWithTooltips || $[28] !== setFocus || $[29] !== t10 || $[30] !== t11 || $[31] !== t12 || $[32] !== t13) {
    t14 = <BywayMapLazy markers={markersWithTooltips} segments={segmentsWithTooltips} route={mapData.route} mode={mode} onModeChange={onModeChange} focus={focus} onFocusChange={setFocus} showLabels={t10} hideInactiveLabels={t11} showDetailedViewButton={t12} activeMarkerId={activeMarkerId} progress={progress} overlayBottomSlot={t13} />;
    $[20] = activeMarkerId;
    $[21] = focus;
    $[22] = mapData.route;
    $[23] = markersWithTooltips;
    $[24] = mode;
    $[25] = onModeChange;
    $[26] = progress;
    $[27] = segmentsWithTooltips;
    $[28] = setFocus;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}
function _temp3(markerId) {
  const {
    itemId
  } = parseMarkerId(markerId);
  if (itemId === undefined) {
    return false;
  }
  return scrollItineraryCardIntoView(itemId);
}
function _temp2(s_0) {
  return s_0.activeItemId;
}
function _temp(s) {
  return s.progress;
}